import { Box, Card, CardContent, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import { Group, School, Class as ClassIcon, Inbox } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { api } from '../../../shared/api';
import { formatINR } from '../../../shared/utils';
import PageHeader from '../../../shared/PageHeader';

export default function AdminDashboard() {
  const { data } = useQuery({ queryKey: ['admin-dashboard'], queryFn: async () => (await api.get('/dashboard/admin')).data });

  return (
    <Box>
      <PageHeader title="Administration" subtitle="School-wide operational overview." />
      <Grid container spacing={2}>
        <Stat icon={<School />} color="#1E3A8A" label="Students" value={data?.students ?? 0} link="/portal/students" />
        <Stat icon={<Group />} color="#10B981" label="Teachers" value={data?.teachers ?? 0} link="/portal/teachers" />
        <Stat icon={<ClassIcon />} color="#F59E0B" label="Classes" value={data?.classes ?? 0} link="/portal/timetable" />
        <Stat icon={<Inbox />} color="#0EA5E9" label="Pending applications" value={data?.pendingApplications ?? 0} link="/portal/notices" />

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Fee Collection</Typography>
              <Stack direction="row" spacing={4} sx={{ mt: 2 }}>
                <Box>
                  <Typography variant="overline" color="text.secondary">Total due</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{formatINR(data?.feeCollection?.totalDue ?? 0)}</Typography>
                </Box>
                <Box>
                  <Typography variant="overline" color="text.secondary">Collected</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>{formatINR(data?.feeCollection?.totalPaid ?? 0)}</Typography>
                </Box>
                <Box>
                  <Typography variant="overline" color="text.secondary">Outstanding</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>
                    {formatINR((data?.feeCollection?.totalDue ?? 0) - (data?.feeCollection?.totalPaid ?? 0))}
                  </Typography>
                </Box>
              </Stack>
              <LinearProgress sx={{ mt: 2, height: 12, borderRadius: 1 }} value={data?.feeCollection?.percent ?? 0} variant="determinate" />
              <Typography variant="caption" color="text.secondary">{data?.feeCollection?.percent ?? 0}% collected</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Quick links</Typography>
              <Stack spacing={1} sx={{ mt: 2 }}>
                <QuickLink to="/portal/students/bulk" label="Bulk upload students" />
                <QuickLink to="/portal/notices" label="Publish notice" />
                <QuickLink to="/portal/fees" label="Record payment" />
                <QuickLink to="/portal/audit-log" label="Audit log" />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

function Stat({ icon, color, label, value, link }: any) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Card component={RouterLink} to={link} sx={{ textDecoration: 'none', display: 'block' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ color, bgcolor: `${color}10`, p: 1, borderRadius: 2 }}>{icon}</Box>
            <Box>
              <Typography variant="overline" color="text.secondary">{label}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>{value}</Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}
function QuickLink({ to, label }: { to: string; label: string }) {
  return (
    <Box component={RouterLink} to={to} sx={{ textDecoration: 'none', color: 'primary.main', fontWeight: 600, p: 1, borderRadius: 1, '&:hover': { bgcolor: '#F1F5F9' } }}>
      → {label}
    </Box>
  );
}
